<script>
/**
 * @component HealthConversationResultDetail
 * @description Vista del resultado de una HealthConversationTask completada.
 *              Header con score grande + semáforo + closedReason.
 *              Bloque de justificación de IA.
 *              Tabla de señales detectadas (criterio · valor · severidad).
 *              Transcript completo colapsable.
 *
 *              Carga el transcript on-demand desde el endpoint
 *              `/health-conversations/:id` (no viene populado en la task).
 *
 * @example <HealthConversationResultDetail :task="task" />
 */
import { formatAR } from "@/services/datetime";
import HealthScoreBadge from "@/components/ui/HealthScoreBadge.vue";
import healthConversationService from "@/services/health-conversation";

const CLOSED_REASON_LABELS = {
  ai_sufficient: "IA detectó info suficiente",
  max_turns: "Llegó al tope de turnos",
  patient_silent: "Paciente no respondió",
  urgent: "Urgencia detectada por IA",
  error: "Error en el evaluador",
};

const SEVERITY_LABELS = {
  low: "Baja",
  mid: "Media",
  high: "Alta",
};

export default {
  name: "HealthConversationResultDetail",

  components: { HealthScoreBadge },

  props: {
    /**
     * @description HealthConversationTask doc (con .result populado).
     */
    task: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      conversation: null,
      loadingTranscript: false,
      transcriptError: null,
      transcriptOpen: false,
    };
  },

  computed: {
    result() {
      return this.task.result || {};
    },
    score() {
      return this.result.score;
    },
    semaphore() {
      return this.result.semaphore;
    },
    closedReasonLabel() {
      return CLOSED_REASON_LABELS[this.result.closedReason] || "—";
    },
    turnsUsed() {
      return this.result.turnsUsed || 0;
    },
    justification() {
      return this.result.aiJustification || "";
    },
    detectedSignals() {
      return Array.isArray(this.result.detectedSignals)
        ? this.result.detectedSignals
        : [];
    },
    /**
     * @description UC-REG-079: TZ del tenant. El campo se llama
     *              completedAtArg para hacer explicito que es hora local
     *              Argentina; ahora respeta tambien tenants futuros con
     *              otra TZ.
     */
    completedAtArg() {
      if (!this.task.completedAt) return "";
      return formatAR(this.task.completedAt, "DD MMM YYYY HH:mm");
    },
    conversationId() {
      return this.result.conversationId;
    },
    transcriptTurns() {
      if (!this.conversation || !Array.isArray(this.conversation.turns))
        return [];
      return this.conversation.turns;
    },
  },

  methods: {
    /**
     * @description Carga el transcript on-demand cuando el médico expande.
     * @returns {Promise<void>}
     */
    async loadTranscript() {
      if (!this.conversationId || this.conversation) return;
      this.loadingTranscript = true;
      this.transcriptError = null;
      try {
        const { data } = await healthConversationService.get(
          this.conversationId
        );
        this.conversation = data;
      } catch (e) {
        this.transcriptError = "No se pudo cargar el transcript.";
      } finally {
        this.loadingTranscript = false;
      }
    },
    /**
     * @description Toggle del collapse + lazy load.
     */
    onToggleTranscript() {
      this.transcriptOpen = !this.transcriptOpen;
      if (this.transcriptOpen) this.loadTranscript();
    },
    /**
     * @description Etiqueta legible de severidad.
     */
    severityLabel(s) {
      return SEVERITY_LABELS[s] || s;
    },
    /**
     * @description UC-REG-079: hora del turn en TZ del tenant.
     */
    formatTurnTime(at) {
      if (!at) return "";
      return formatAR(at, "HH:mm");
    },
  },
};
</script>

<template lang="pug">
  .health-conversation-result
    //- HEADER: score grande + semáforo + cierre
    .health-conversation-result__header
      HealthScoreBadge(:score="score" :semaphore="semaphore" size="lg")
      .health-conversation-result__meta
        .meta-line(v-if="closedReasonLabel")
          span.meta-line__key Cierre:
          span.meta-line__val {{ closedReasonLabel }}
        .meta-line
          span.meta-line__key Turnos:
          span.meta-line__val {{ turnsUsed }}
        .meta-line(v-if="completedAtArg")
          span.meta-line__key Fecha:
          span.meta-line__val {{ completedAtArg }}

    //- JUSTIFICACIÓN
    .health-conversation-result__section(v-if="justification")
      h4.section-title Justificación de la IA
      p.section-body {{ justification }}

    //- SEÑALES
    .health-conversation-result__section(v-if="detectedSignals.length > 0")
      h4.section-title Señales detectadas
      el-table(:data="detectedSignals" size="small" border)
        el-table-column(prop="criterion" label="Criterio" min-width="120")
        el-table-column(prop="value" label="Valor reportado" min-width="240")
        el-table-column(label="Severidad" width="120")
          template(slot-scope="scope")
            span.severity-chip(:class="`severity-${scope.row.severity}`") {{ severityLabel(scope.row.severity) }}

    //- TRANSCRIPT
    .health-conversation-result__section(v-if="conversationId")
      .transcript-toggle(@click="onToggleTranscript" role="button" tabindex="0")
        i.el-icon-arrow-right(v-if="!transcriptOpen")
        i.el-icon-arrow-down(v-else)
        span Ver transcript completo
      .transcript-loading(v-if="loadingTranscript") Cargando…
      .transcript-error(v-if="transcriptError") {{ transcriptError }}
      .transcript-body(v-if="transcriptOpen && conversation")
        .turn(
          v-for="(turn, idx) in transcriptTurns"
          :key="`turn-${idx}`"
          :class="`turn--${turn.role}`"
        )
          .turn__avatar
            template(v-if="turn.role === 'bot'") 🤖
            template(v-else) 👤
          .turn__bubble
            .turn__text {{ turn.text }}
            .turn__time {{ formatTurnTime(turn.at) }}
</template>

<style lang="scss" scoped>
.health-conversation-result {
  font-family: "Plus Jakarta Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;

  &__header {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px;
    background: #f7f9fc;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
  }
  &__meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

.meta-line {
  font-size: 13px;
  color: #4b5563;
  &__key {
    margin-right: 6px;
    font-weight: 500;
  }
  &__val {
    color: #111827;
    font-weight: 600;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}
.section-body {
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
  margin: 0;
  white-space: pre-line;
}

.severity-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  &.severity-low {
    color: #1f8a4c;
    background: #ebf7f0;
  }
  &.severity-mid {
    color: #b07c0a;
    background: #fbf3e0;
  }
  &.severity-high {
    color: #b3261e;
    background: #fbe9e7;
  }
}

.transcript-toggle {
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1d3557;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  user-select: none;
  &:hover {
    color: #0f1e36;
  }
}
.transcript-loading,
.transcript-error {
  font-size: 13px;
  color: #6b7280;
  padding: 8px 0;
}
.transcript-error {
  color: #b3261e;
}

.transcript-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  max-height: 480px;
  overflow-y: auto;
}

.turn {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  &--bot {
    justify-content: flex-start;
  }
  &--patient {
    justify-content: flex-end;
    flex-direction: row-reverse;
  }
  &__avatar {
    font-size: 18px;
    line-height: 1;
    padding-top: 4px;
  }
  &__bubble {
    max-width: 78%;
    padding: 8px 12px;
    border-radius: 12px;
    background: white;
    border: 1px solid #e5e7eb;
  }
  &--patient &__bubble {
    background: #eef2ff;
    border-color: #c7d2fe;
  }
  &__text {
    font-size: 13px;
    line-height: 1.5;
    color: #111827;
    white-space: pre-line;
  }
  &__time {
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
    text-align: right;
  }
}
</style>
